import React, { useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import { ColDef } from 'ag-grid-community';
import { Calendar, Clock, IceCreamCone, CreditCard, Receipt, ShoppingCart } from 'lucide-react';
import GelatoDataGrid from '../components/DataGrid/GelatoDataGrid';
import apiService from '../services/api';
import {
  PageContainer,
  PageHeader,
  Alert,
  Button,
  Input,
  LoadingState,
} from '../components/ui';

type Tab = 'totali' | 'orari' | 'prodotti' | 'testate' | 'venduto' | 'pagamenti';

const fmtEuro = (val: any) =>
  val != null ? `€ ${Number(val).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-';
const fmtDateStr = (val: string) => val ? new Date(val).toLocaleDateString('it-IT') : '-';

// Colori di dominio per i tipi di pagamento (legenda/barre)
const COLORI: Record<string, string> = {
  'CONTANTI': 'var(--success)',
  'CARTA DI CREDITO': 'var(--info)',
  'BANCOMAT': 'var(--primary-600)',
  'ALTRI PAG. ELETTR.': 'var(--accent-700)',
  'BUONI PASTO': 'var(--warning)',
  'TICKET': 'var(--warning)',
};
const getColor = (pag: string) => COLORI[pag?.toUpperCase()] || 'var(--gray-400)';

const FiltersBar = styled.div`
  background: var(--card-bg);
  border: 1px solid var(--gray-200);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-sm);
  padding: var(--space-sm) var(--space-lg);
  margin-bottom: var(--space-lg);
  display: flex;
  align-items: center;
  gap: var(--space-md);
  flex-wrap: wrap;
`;

const DateLabel = styled.label`
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--gray-700);
`;

const FilterDivider = styled.span`
  color: var(--gray-200);
  margin: 0 var(--space-xs);
`;

const TabsRow = styled.div`
  display: flex;
  gap: var(--space-xs);
  flex-wrap: wrap;
`;

const TabButton = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: var(--space-xs);
  padding: var(--space-sm) var(--space-md);
  border: none;
  border-radius: var(--border-radius-md) var(--border-radius-md) 0 0;
  cursor: pointer;
  font-family: var(--font-family);
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-sm);
  background: ${({ $active }) => ($active ? 'var(--button-primary-bg)' : 'var(--gray-200)')};
  color: ${({ $active }) => ($active ? 'var(--button-primary-text)' : 'var(--gray-700)')};
  transition: background-color var(--transition-fast), color var(--transition-fast);

  svg {
    width: 1em;
    height: 1em;
  }
`;

const ContentCard = styled.div`
  background: var(--card-bg);
  border: 1px solid var(--gray-200);
  border-radius: 0 var(--border-radius-lg) var(--border-radius-lg) var(--border-radius-lg);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
`;

const Pad = styled.div`
  padding: var(--space-lg);
`;

const SectionTitle = styled.h3`
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
  margin: 0 0 var(--space-md);
  color: var(--text-primary);
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-lg);

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SummaryStrip = styled.div`
  padding: var(--space-sm) var(--space-lg);
  background: var(--gray-50);
  border-bottom: 1px solid var(--gray-200);
  font-size: var(--font-size-sm);
  display: flex;
  gap: var(--space-xl);
  flex-wrap: wrap;
`;

const SuccessStrong = styled.strong`
  color: var(--success);
`;

const HourRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  margin-bottom: var(--space-sm);
`;

const HourLabel = styled.span`
  width: 50px;
  text-align: right;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--gray-700);
`;

const BarTrack = styled.div`
  flex: 1;
  background: var(--gray-200);
  border-radius: var(--border-radius-sm);
  height: 24px;
  overflow: hidden;
`;

const BarFill = styled.div<{ $width: string; $color?: string }>`
  width: ${({ $width }) => $width};
  background: ${({ $color }) => $color || 'var(--primary-600)'};
  height: 100%;
  border-radius: var(--border-radius-sm);
  min-width: 2px;
`;

const HourTotal = styled.span`
  width: 90px;
  font-size: var(--font-size-sm);
  color: var(--success);
  font-weight: var(--font-weight-semibold);
`;

const HourCount = styled.span`
  width: 80px;
  font-size: var(--font-size-xs);
  color: var(--text-secondary);
`;

const PaymentRow = styled.div`
  margin-bottom: var(--space-sm);
`;

const PaymentHead = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: var(--font-size-sm);
  margin-bottom: 0.2rem;
`;

const PaymentName = styled.span`
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
`;

const PaymentPerc = styled.span`
  color: var(--text-secondary);
  font-weight: var(--font-weight-normal);
`;

const PaymentTotalRow = styled.div`
  margin-top: var(--space-md);
  padding-top: var(--space-sm);
  border-top: 2px solid var(--gray-200);
  display: flex;
  justify-content: space-between;
  font-weight: var(--font-weight-bold);
`;

const PaymentDot = styled.span<{ $color: string }>`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  margin-right: 6px;
`;

const EmptyBox = styled.div`
  padding: var(--space-2xl);
  text-align: center;
  color: var(--text-secondary);
`;

const Empty: React.FC<{ msg?: string }> = ({ msg }) => (
  <EmptyBox>{msg || 'Nessun dato per il periodo selezionato.'}</EmptyBox>
);

const KiaraDatiPage: React.FC = () => {
  const [tab, setTab] = useState<Tab>('totali');
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  const [dateFrom, setDateFrom] = useState(thirtyDaysAgo.toISOString().slice(0, 10));
  const [dateTo, setDateTo] = useState(new Date().toISOString().slice(0, 10));

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      let res: any;
      if (tab === 'totali' || tab === 'orari') {
        res = await apiService.getKiaraStatsTotali(dateFrom, dateTo);
      } else if (tab === 'prodotti') {
        res = await apiService.getKiaraStatsProdotti(dateFrom, dateTo);
      } else if (tab === 'testate') {
        res = await apiService.getKiaraDatiTestate(dateFrom, dateTo);
      } else if (tab === 'venduto') {
        res = await apiService.getKiaraDatiVenduto(dateFrom, dateTo);
      } else if (tab === 'pagamenti') {
        res = await apiService.getKiaraStatsPagamenti(dateFrom, dateTo);
      }
      setData(res);
    } catch (err: any) {
      setError(err.message || 'Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch solo al cambio di tab/date; loadData è ricreata ad ogni render
  useEffect(() => { loadData(); }, [tab, dateFrom, dateTo]);

  const fmt = fmtEuro;

  const tabs: { id: Tab; label: string; icon: React.ReactNode }[] = [
    { id: 'totali',    label: 'Giorno/Mese', icon: <Calendar /> },
    { id: 'orari',     label: 'Ore', icon: <Clock /> },
    { id: 'prodotti',  label: 'Prodotti', icon: <IceCreamCone /> },
    { id: 'pagamenti', label: 'Pagamenti', icon: <CreditCard /> },
    { id: 'testate',   label: 'Scontrini', icon: <Receipt /> },
    { id: 'venduto',   label: 'Venduto', icon: <ShoppingCart /> },
  ];

  const setQuickDate = (days: number) => {
    const to = new Date();
    const from = new Date();
    if (days === 0) {
      setDateFrom(to.toISOString().slice(0, 10));
      setDateTo(to.toISOString().slice(0, 10));
    } else if (days === 1) {
      from.setDate(from.getDate() - 1);
      setDateFrom(from.toISOString().slice(0, 10));
      setDateTo(from.toISOString().slice(0, 10));
    } else if (days === -1) {
      const yearStart = new Date(new Date().getFullYear(), 0, 1);
      setDateFrom(yearStart.toISOString().slice(0, 10));
      setDateTo(to.toISOString().slice(0, 10));
    } else {
      from.setDate(from.getDate() - days);
      setDateFrom(from.toISOString().slice(0, 10));
      setDateTo(to.toISOString().slice(0, 10));
    }
  };

  // ── Column Defs ──────────────────────────────────────────────
  const colsGiornalieri = useMemo(() => [
    { headerName: 'Data', field: 'data', valueFormatter: p => fmtDateStr(p.value), minWidth: 110 },
    { headerName: 'Scontrini', field: 'num_scontrini', type: 'numericColumn', minWidth: 100 },
    { headerName: 'Totale', field: 'totale', valueFormatter: p => fmtEuro(p.value), type: 'numericColumn', cellStyle: { color: 'var(--success)', fontWeight: 600 }, minWidth: 130 },
    { headerName: 'Medio', field: 'scontrino_medio', valueFormatter: p => fmtEuro(p.value), type: 'numericColumn', cellStyle: { color: 'var(--gray-500)' }, minWidth: 120 },
  ] as ColDef[], []);

  const colsMensili = useMemo(() => [
    { headerName: 'Mese', field: 'mese', minWidth: 110, cellStyle: { fontWeight: 600 } },
    { headerName: 'Giorni', field: 'giorni_attivi', type: 'numericColumn', minWidth: 80 },
    { headerName: 'Scontrini', field: 'num_scontrini', type: 'numericColumn', minWidth: 100 },
    { headerName: 'Totale', field: 'totale', valueFormatter: p => fmtEuro(p.value), type: 'numericColumn', cellStyle: { color: 'var(--success)', fontWeight: 600 }, minWidth: 130 },
    { headerName: 'Media/gg', valueGetter: p => p.data.giorni_attivi > 0 ? Number(p.data.totale) / Number(p.data.giorni_attivi) : null, valueFormatter: p => fmtEuro(p.value), type: 'numericColumn', cellStyle: { color: 'var(--gray-500)' }, minWidth: 120 },
  ] as ColDef[], []);

  const colsProdotti = useMemo(() => [
    { headerName: '#', valueGetter: p => (p.node?.rowIndex ?? 0) + 1, width: 55, sortable: false, filter: false },
    { headerName: 'Prodotto', field: 'prodotto', minWidth: 140, cellStyle: { fontWeight: 600 } },
    { headerName: 'Descrizione', field: 'descrizione', minWidth: 200 },
    { headerName: 'Quantità', field: 'totale_quantita', valueFormatter: p => p.value != null ? Number(p.value).toFixed(0) : '-', type: 'numericColumn', minWidth: 100 },
    { headerName: 'Totale', field: 'totale_importo', valueFormatter: p => fmtEuro(p.value), type: 'numericColumn', cellStyle: { color: 'var(--success)', fontWeight: 600 }, minWidth: 130 },
    { headerName: '% Media', field: 'perc_media', valueFormatter: p => p.value != null ? `${Number(p.value).toFixed(1)}%` : '-', type: 'numericColumn', cellStyle: { color: 'var(--gray-500)' }, minWidth: 100 },
    { headerName: 'Giorni', field: 'giorni_venduto', type: 'numericColumn', minWidth: 80 },
  ] as ColDef[], []);

  const colsTestate = useMemo(() => [
    { headerName: 'Data', field: 'data_riferimento', valueFormatter: p => fmtDateStr(p.value), minWidth: 110 },
    { headerName: 'Ora', field: 'data_scontrino', valueFormatter: p => p.value ? new Date(p.value).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }) : '-', minWidth: 80 },
    { headerName: 'N° Scontrino', field: 'id_scontrini_testate', minWidth: 150, cellStyle: { fontFamily: 'var(--font-family-mono)', fontSize: '0.78rem' } },
    { headerName: 'Cassa', field: 'cassa', minWidth: 100 },
    { headerName: 'Utente', field: 'utente', minWidth: 120 },
    { headerName: 'Causale', field: 'causale', minWidth: 120 },
    { headerName: 'Importo', field: 'importo', valueFormatter: p => fmtEuro(p.value), type: 'numericColumn', cellStyle: (p: any) => ({ color: Number(p.value) >= 0 ? 'var(--success)' : 'var(--error)', fontWeight: 600 }), minWidth: 130 },
  ] as ColDef[], []);

  const colsVenduto = useMemo(() => [
    { headerName: 'Data', field: 'data_riferimento', valueFormatter: p => fmtDateStr(p.value), minWidth: 110 },
    { headerName: 'Prodotto', field: 'prodotto', minWidth: 140, cellStyle: { fontWeight: 500 } },
    { headerName: 'Descrizione', field: 'descrizione', minWidth: 200 },
    { headerName: 'Q.tà', field: 'quantita', valueFormatter: p => p.value != null ? Number(p.value).toFixed(0) : '-', type: 'numericColumn', minWidth: 80 },
    { headerName: 'Importo', field: 'importo', valueFormatter: p => fmtEuro(p.value), type: 'numericColumn', cellStyle: { color: 'var(--success)', fontWeight: 600 }, minWidth: 130 },
    { headerName: '%', field: 'perc', valueFormatter: p => p.value != null ? `${Number(p.value).toFixed(1)}%` : '-', type: 'numericColumn', cellStyle: { color: 'var(--gray-500)' }, minWidth: 80 },
  ] as ColDef[], []);

  const colsPerCassa = useMemo(() => [
    { headerName: 'Cassa', valueGetter: p => p.data.utente || `Cassa ${p.data.id_cassa}`, minWidth: 130, cellStyle: { fontWeight: 600 } },
    { headerName: 'Pagamento', field: 'pagamento', minWidth: 180, cellRenderer: (p: any) => (
      <span><PaymentDot $color={getColor(p.value)} />{p.value || '-'}</span>
    )},
    { headerName: 'Totale', field: 'totale', valueFormatter: p => fmtEuro(p.value), type: 'numericColumn', cellStyle: (p: any) => ({ color: getColor(p.data?.pagamento), fontWeight: 600 }), minWidth: 140 },
  ] as ColDef[], []);

  return (
    <PageContainer>
      <PageHeader title="Dati Cassa (Kiara)" />

      {/* Filtri */}
      <FiltersBar>
        <DateLabel>
          Dal: <Input type="date" value={dateFrom} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDateFrom(e.target.value)} />
        </DateLabel>
        <DateLabel>
          Al: <Input type="date" value={dateTo} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDateTo(e.target.value)} />
        </DateLabel>
        <Button onClick={loadData}>Aggiorna</Button>
        <FilterDivider>|</FilterDivider>
        {[{ label: 'Oggi', days: 0 }, { label: 'Ieri', days: 1 }, { label: '7 gg', days: 7 }, { label: '30 gg', days: 30 }, { label: '90 gg', days: 90 }, { label: 'Anno', days: -1 }].map(({ label, days }) => (
          <Button key={label} $variant="secondary" $size="sm" onClick={() => setQuickDate(days)}>
            {label}
          </Button>
        ))}
      </FiltersBar>

      {/* Tabs */}
      <TabsRow>
        {tabs.map(t => (
          <TabButton key={t.id} $active={tab === t.id} onClick={() => setTab(t.id)}>
            {t.icon}
            {t.label}
          </TabButton>
        ))}
      </TabsRow>

      {/* Contenuto */}
      <ContentCard>
        {error && (
          <Pad style={{ paddingBottom: 0 }}>
            <Alert $variant="error">{error}</Alert>
          </Pad>
        )}
        {loading ? (
          <LoadingState />
        ) : (
          <>
            {/* Totali giorno/mese */}
            {tab === 'totali' && (
              <Pad>
                <TwoCol>
                  <div>
                    <SectionTitle>Totali per Giorno</SectionTitle>
                    <GelatoDataGrid rowData={data?.giornalieri || []} columnDefs={colsGiornalieri} height="450px" paginationPageSize={30} exportable={true} csvFileName="totali-giorno-kiara" />
                  </div>
                  <div>
                    <SectionTitle>Totali per Mese</SectionTitle>
                    <GelatoDataGrid rowData={data?.mensili || []} columnDefs={colsMensili} height="450px" pagination={false} exportable={true} csvFileName="totali-mese-kiara" />
                  </div>
                </TwoCol>
              </Pad>
            )}

            {/* Distribuzione oraria */}
            {tab === 'orari' && (
              <Pad>
                <SectionTitle>Distribuzione Oraria degli Scontrini</SectionTitle>
                {!data?.orari?.length ? (
                  <Empty msg="Nessun dato orario — disponibile dopo il prossimo import con timestamp corretto." />
                ) : (() => {
                  const maxTot = Math.max(...data.orari.map((r: any) => Number(r.totale) || 0));
                  return (
                    <div>
                      {data.orari.map((r: any) => (
                        <HourRow key={r.ora}>
                          <HourLabel>{String(r.ora).padStart(2, '0')}:00</HourLabel>
                          <BarTrack>
                            <BarFill $width={`${(Number(r.totale) / maxTot) * 100}%`} />
                          </BarTrack>
                          <HourTotal>{fmt(r.totale)}</HourTotal>
                          <HourCount>{r.num_scontrini} scontrini</HourCount>
                        </HourRow>
                      ))}
                    </div>
                  );
                })()}
              </Pad>
            )}

            {/* Prodotti */}
            {tab === 'prodotti' && (
              <Pad>
                <SectionTitle>Prodotti più Venduti (periodo selezionato)</SectionTitle>
                <GelatoDataGrid rowData={data?.perProdotto || []} columnDefs={colsProdotti} height="520px" exportable={true} csvFileName="prodotti-kiara" />
              </Pad>
            )}

            {/* Scontrini dettaglio */}
            {tab === 'testate' && (
              <div>
                {(data?.data?.length ?? 0) > 0 && (
                  <SummaryStrip>
                    <span><strong>{data.data.length}</strong> scontrini</span>
                    <span>Totale: <SuccessStrong>{fmt(data.data.reduce((s: number, r: any) => s + (Number(r.importo) || 0), 0))}</SuccessStrong></span>
                  </SummaryStrip>
                )}
                <Pad>
                  <GelatoDataGrid rowData={data?.data || []} columnDefs={colsTestate} height="560px" exportable={true} csvFileName="scontrini-kiara" />
                </Pad>
              </div>
            )}

            {/* Venduto dettaglio */}
            {tab === 'venduto' && (
              <div>
                {(data?.data?.length ?? 0) > 0 && (
                  <SummaryStrip>
                    <span><strong>{data.data.length}</strong> righe</span>
                    <span>Totale: <SuccessStrong>{fmt(data.data.reduce((s: number, r: any) => s + (Number(r.importo) || 0), 0))}</SuccessStrong></span>
                  </SummaryStrip>
                )}
                <Pad>
                  <GelatoDataGrid rowData={data?.data || []} columnDefs={colsVenduto} height="560px" exportable={true} csvFileName="venduto-kiara" />
                </Pad>
              </div>
            )}

            {/* Pagamenti per tipo */}
            {tab === 'pagamenti' && (
              <Pad>
                {!data?.perTipo?.length ? (
                  <Empty msg="Nessun dato pagamenti — aggiungi 'vwcassa' a KIARA_ROUTES e rieffettua l'import." />
                ) : (() => {
                  const totaleGen = data.perTipo.reduce((s: number, r: any) => s + (Number(r.totale) || 0), 0);
                  return (
                    <TwoCol>
                      {/* Bar chart visuale */}
                      <div>
                        <SectionTitle>Distribuzione per Tipo</SectionTitle>
                        {data.perTipo.map((r: any) => {
                          const perc = totaleGen > 0 ? (Number(r.totale) / totaleGen) * 100 : 0;
                          const col = getColor(r.pagamento);
                          return (
                            <PaymentRow key={r.pagamento}>
                              <PaymentHead>
                                <PaymentName>{r.pagamento || '-'}</PaymentName>
                                <span style={{ color: col, fontWeight: 700 }}>{fmt(r.totale)} <PaymentPerc>({perc.toFixed(1)}%)</PaymentPerc></span>
                              </PaymentHead>
                              <BarTrack style={{ height: 20 }}>
                                <BarFill $width={`${perc}%`} $color={col} />
                              </BarTrack>
                            </PaymentRow>
                          );
                        })}
                        <PaymentTotalRow>
                          <span>TOTALE</span>
                          <SuccessStrong>{fmt(totaleGen)}</SuccessStrong>
                        </PaymentTotalRow>
                      </div>
                      {/* AG Grid per cassa */}
                      <div>
                        <SectionTitle>Dettaglio per Cassa</SectionTitle>
                        <GelatoDataGrid rowData={data?.perCassa || []} columnDefs={colsPerCassa} height="380px" pagination={false} />
                      </div>
                    </TwoCol>
                  );
                })()}
              </Pad>
            )}
          </>
        )}
      </ContentCard>
    </PageContainer>
  );
};

export default KiaraDatiPage;
